// Member-identity hydrator — the guarantee behind "I must NEVER see an
// invited user without a display name or correct avatar."
//
// Every member of a conversation (a group's roster, or a 1:1 peer) is resolved
// from the directory by ACCOUNT ID — `getUserProfile(accountId)` returns the
// real display name + avatar media id, and the directory keys on accountId, so
// a member with no cached name still resolves (the old group path searched by
// NAME and silently skipped anyone whose name we hadn't learned yet). The
// resolved name + avatar URL are PERSISTED onto the Dexie conversation row, so:
//   * the roster / member bubbles / Chat Info render the real identity,
//   * it survives reload AND offline (it's on the row, not in process memory),
//   * it's ready BEFORE the user opens Chat Info.
//
// Idempotent and safe to call on every chat open / reconnect: the merge only
// writes non-empty changes and never blanks a previously-good value.

import { urlB64NoPadToBytes } from '@ping/ui';
import { parseGroupConvMeta } from '@ping/ui/src/conversation/groupConversation';
import { canonicalDeviceIdHex, hexToBytes } from '@ping/ui/src/conversation/mlsBytes';
import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';
import { withAvatarCacheKey } from '@ping/ui/src/sync/avatarCacheBust';
import { getCurrentUserAccountId } from '@ping/ui/src/sync/registerCurrentUser';

import { log } from '@core/log/log';
import type { ConversationMembership } from '@core/network/users';
import {
  fetchConversationMembership,
  getUserProfile,
  getUserProfiles,
  listAccountDeviceIds,
} from '@core/network/users';
import {
  clearConversationGroupAvatar,
  dedupeDirectConversationsByPeer,
  get as getConversationRow,
  list as listConversationRows,
  markConversationAsGroup,
  markConversationServerOrphaned,
  mergeMemberDeviceMaps,
  mergeMemberProfiles,
  setConversationAvatar,
  setConversationAvatarMediaId,
  setConversationIdentityVerified,
  setConversationMemberAccountIds,
  setConversationName,
  setConversationPeerAccountId,
} from '@core/persistence/conversations';

import { fetchMediaDownloadUrl } from './mediaUpload';

// Settle window before an authoritative `not_member` (403/404) may orphan a
// conversation. A legit receiver-join has its server membership recorded BEFORE
// the Welcome is fanned out (so it 200s), and a creator's row carries a
// resolvable peer (so it never reaches the members path) — but this grace guards
// the rare window where a just-created group's Commit hasn't uploaded yet
// (`no_transport`) and its own membership row therefore isn't written. Only a row
// that STILL 403s past this window is a true dead orphan. Mirrors the
// `DEDUP_SETTLE_MS` discipline in `core/persistence/conversations`.
const ORPHAN_SETTLE_MS = 15_000;

/** True once a row is old enough that a `not_member` verdict is trustworthy (not
 *  an in-flight create/join still settling server-side). */
function orphanSettled(createdAt: number | undefined): boolean {
  return Date.now() - (createdAt ?? 0) >= ORPHAN_SETTLE_MS;
}

/**
 * Read the SDK's MLS group meta for a conversation and return its group fields if
 * it is a GROUP, else null. The MLS GroupContext `name` carries the
 * `ping:meta:v1` blob (`sp:true` / title / avatarMediaId) — the authoritative,
 * broadcast-independent group marker. Synchronous + best-effort (the JS SDK caches
 * conversations in memory). This is what lets hydration distinguish a single-peer
 * group from a 1:1 (they are roster-identical) so it never stamps the peer's
 * avatar as the group avatar.
 */
function readSdkGroupMeta(
  conversationIdHex: string,
): { name?: string; avatarMediaId?: string } | null {
  const status = useSessionStore.getState().status;
  if (status.kind !== 'authenticated') return null;
  const client = status.client as unknown as {
    getConversation?: (id: Uint8Array) => { name?: string } | undefined;
  };
  if (typeof client.getConversation !== 'function') return null;
  let name: string | undefined;
  try {
    name = client.getConversation(hexToBytes(conversationIdHex))?.name;
  } catch {
    return null;
  }
  if (name === undefined || name.length === 0) return null;
  const meta = parseGroupConvMeta(name);
  if (meta === null) return null;
  const out: { name?: string; avatarMediaId?: string } = {};
  if (meta.title !== undefined && meta.title.length > 0) out.name = meta.title;
  if (meta.avatarMediaId !== undefined && meta.avatarMediaId.length > 0) {
    out.avatarMediaId = meta.avatarMediaId;
  }
  return out;
}

// A hex device id (32+ hex chars) is NOT an account id — passing it to the
// directory 400s. The roster should only ever hold base64url account ids, but
// guard anyway so a stray device id can't spam failed requests.
const HEX_DEVICE_ID = /^[0-9a-fA-F]{32,}$/;

// The directory keys on the 16-byte server ACCOUNT id (`auth` `account_id`),
// NOT the 32-byte MLS `UserId` (SHA256 of the identity pubkey) that the SDK
// roster exposes. They are different id spaces and there is no client-side
// mapping — the 16-byte account id only ever reaches us via the `ping.profile`
// broadcast. So only attempt a directory lookup for ids that decode to exactly
// 16 bytes; a 32-byte roster id (base64url ~43 chars) would just 400.
function isDirectoryAccountId(id: string): boolean {
  if (id.length === 0 || HEX_DEVICE_ID.test(id)) return false;
  try {
    return urlB64NoPadToBytes(id).length === 16;
  } catch {
    return false;
  }
}

/**
 * Resolve a 1:1's peer account id from the SERVER's authoritative account
 * roster (`GET /v1/conversations/{id}/members`) — the broadcast-independent
 * source. Returns the single non-self 16-byte account id, or undefined when the
 * roster is unavailable / not an unambiguous 1:1.
 */
async function resolvePeerFromServerRoster(
  conversationId: string,
): Promise<{ membership: ConversationMembership['kind']; peer: string | undefined }> {
  const membership = await fetchConversationMembership(conversationId);
  if (membership.kind !== 'member') return { membership: membership.kind, peer: undefined };
  const self = getCurrentUserAccountId();
  const peers = membership.accountIds.filter((id) => id !== self && isDirectoryAccountId(id));
  return { membership: 'member', peer: peers.length === 1 ? peers[0] : undefined };
}

// 'New chat' / 'Group chat' / empty are placeholders we may overwrite with the
// resolved peer name; a real (or user-set) name is left untouched.
function isPlaceholderName(name: string | undefined): boolean {
  const n = (name ?? '').trim();
  return n.length === 0 || n === 'New chat' || n === 'Group chat';
}

interface ResolvedProfile {
  displayName?: string;
  avatarUri?: string;
  /** Stable media id behind `avatarUri`. Persisted for 1:1s so the peer avatar
   *  can be RE-RESOLVED from it (the `avatarUri` presigned url expires), the same
   *  way group icons are — otherwise the header falls back to initials on expiry. */
  avatarMediaId?: string;
  /** Peer's Persona KYC identity-verified status (drives the verified check). */
  identityVerified?: boolean;
}

async function resolveFromPeer(
  p: Awaited<ReturnType<typeof getUserProfile>>,
): Promise<ResolvedProfile | null> {
  if (p === null) return null;
  let avatarUri: string | undefined;
  if (p.avatarMediaId !== undefined && p.avatarMediaId !== null) {
    // Prefer the presigned URL the backend rode on the profile response — that is
    // what removes the per-member `GET /v1/media/{id}` (opening a 50-person group
    // is 1 batch-profiles call, not 51). Only fall back to resolving the media id
    // ourselves when the backend didn't supply a URL (older deploy / media
    // unwired). Either way, key the URL by the media id so a peer's CHANGED photo
    // (new id) renders fresh instead of being served stale from the image cache.
    const rawUrl =
      p.avatarUrl !== undefined && p.avatarUrl.length > 0
        ? p.avatarUrl
        : await fetchMediaDownloadUrl(p.avatarMediaId).catch(() => undefined);
    avatarUri = withAvatarCacheKey(rawUrl, p.avatarMediaId);
  }
  const out: ResolvedProfile = {};
  if (p.displayName.length > 0) out.displayName = p.displayName;
  if (avatarUri !== undefined && avatarUri.length > 0) out.avatarUri = avatarUri;
  if (p.avatarMediaId !== undefined && p.avatarMediaId !== null && p.avatarMediaId.length > 0) {
    out.avatarMediaId = p.avatarMediaId;
  }
  // Always carry the KYC flag (even when false) so a peer who LOSES verification
  // clears the badge on the next hydration pass.
  out.identityVerified = p.identityVerified === true;
  return Object.keys(out).length > 0 ? out : null;
}

async function resolveOne(accountId: string): Promise<ResolvedProfile | null> {
  // Skip ids that aren't a 16-byte directory account id (e.g. a 32-byte MLS
  // roster UserId stored by an older build) — they'd just 400.
  if (!isDirectoryAccountId(accountId)) return null;
  const p = await getUserProfile(accountId).catch(() => null);
  return resolveFromPeer(p);
}

/** One POST /v1/users/profiles for the roster, then resolve avatar URLs. */
async function resolveMany(
  accountIds: readonly string[],
): Promise<Record<string, ResolvedProfile>> {
  const ids = accountIds.filter(isDirectoryAccountId);
  if (ids.length === 0) return {};
  const byId = await getUserProfiles(ids).catch(() => new Map());
  const profiles: Record<string, ResolvedProfile> = {};
  await mapLimited(ids, 6, async (id) => {
    const resolved = await resolveFromPeer(byId.get(id) ?? null);
    if (resolved) profiles[id] = resolved;
  });
  return profiles;
}

// Bounded-concurrency map so a 200-member group doesn't fire 200 directory
// requests at once.
async function mapLimited<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = cursor++;
      if (i >= items.length) return;
      out[i] = await fn(items[i]!);
    }
  });
  await Promise.all(workers);
  return out;
}

/**
 * Backfill the DEVICE-keyed roster maps (`deviceHex → accountId` /
 * `deviceHex → displayName`) authoritatively from `GET /v1/devices/{account}`.
 *
 * This is the systemic fix for "a group message shows no sender name/avatar" and
 * "the chat-list preview shows `…a3f9c1:` instead of a name": those surfaces
 * resolve a message sender by its DEVICE hex, but the device maps were previously
 * only filled by a peer's best-effort `ping.profile` OR a WebChatRoom effect that
 * runs solely while the chat is OPEN. Running it here — inside `hydrateMemberProfiles`,
 * which fires on boot for every conversation, on open, and on reconnect — fills the
 * maps for EVERY member's EVERY device, so a silent/offline member still resolves.
 *
 * Only touches accounts that have no mapped device yet (a device set is stable, so
 * once mapped this is a network no-op). Bounded concurrency, fully best-effort:
 * a peer whose device list is unavailable simply stays unresolved until next pass.
 * Returns true if the row changed.
 */
// In-memory negative cache for the `/v1/devices/{account}` fallback. An account
// that returns NO devices (deleted/suspended, or all devices revoked) would
// otherwise be re-fetched on EVERY hydration pass forever, since it never becomes
// "mapped". Remember it and skip re-fetching for a cooldown. In-memory only
// (resets on reload, so a returning account is re-checked once) — no schema, and
// no risk of durably pinning a stale "gone" verdict. A `force` pass (a real
// message arrived from the account) bypasses the backoff.
const deviceLookupBackoff = new Map<string, number>();
const DEVICE_LOOKUP_BACKOFF_MS = 60 * 60 * 1000; // 1h

function inDeviceLookupBackoff(accountId: string): boolean {
  const at = deviceLookupBackoff.get(accountId);
  if (at === undefined) return false;
  if (Date.now() - at >= DEVICE_LOOKUP_BACKOFF_MS) {
    deviceLookupBackoff.delete(accountId);
    return false;
  }
  return true;
}

export async function backfillGroupDeviceMaps(
  conversationId: string,
  accountIds: readonly string[],
  displayNames: Readonly<Record<string, string>>,
  deviceAccounts: Readonly<Record<string, string>>,
  force = false,
): Promise<boolean> {
  const mappedAccounts = new Set(Object.values(deviceAccounts));
  // Default: skip accounts that already have a mapped device (a device set is
  // stable → network no-op) or that are in the negative-lookup backoff. `force`
  // re-fetches ALL accounts (bypassing both), for the caller that just saw a
  // message from a device it can't resolve — that member may have LINKED A NEW
  // device we don't have a mapping for. `mergeMemberDeviceMaps` still only writes
  // real changes, so a forced re-fetch of an already-mapped device is a no-op.
  const need = accountIds.filter((id) => {
    if (!isDirectoryAccountId(id)) return false;
    if (force) return true;
    return !mappedAccounts.has(id) && !inDeviceLookupBackoff(id);
  });
  if (need.length === 0) return false;
  const namePatch: Record<string, string> = {};
  const accountPatch: Record<string, string> = {};
  await mapLimited(need, 6, async (accountId) => {
    let deviceIds: readonly string[];
    try {
      deviceIds = await listAccountDeviceIds(accountId);
    } catch {
      return; // Transient (network/5xx) — retry next pass, do NOT back off.
    }
    if (deviceIds.length === 0) {
      // No devices → a gone/emptied account; back off so we don't re-hammer it.
      deviceLookupBackoff.set(accountId, Date.now());
      return;
    }
    deviceLookupBackoff.delete(accountId); // resolved → clear any prior backoff
    const displayName = displayNames[accountId];
    for (const deviceHex of deviceIds) {
      const canon = canonicalDeviceIdHex(deviceHex) ?? deviceHex;
      accountPatch[canon] = accountId;
      if (displayName !== undefined && displayName.length > 0) {
        namePatch[canon] = displayName;
      }
    }
  });
  if (Object.keys(accountPatch).length === 0 && Object.keys(namePatch).length === 0) {
    return false;
  }
  return mergeMemberDeviceMaps(conversationId, {
    ...(Object.keys(namePatch).length > 0 ? { memberDeviceNames: namePatch } : {}),
    ...(Object.keys(accountPatch).length > 0
      ? { memberDeviceAccounts: accountPatch }
      : {}),
  });
}

// Per-conversation coalescing state for `hydrateMemberProfiles`. Hydration is
// invoked from MANY independent triggers that can fire in a burst for the SAME
// conversation — the SDK's `onConversationUpdated` re-subscribe/replay storm (via
// `onConversationUpserted`), the bootstrap reconciliation sweep, the open-chat
// effect, `hydrateAllConversations`, and the `online` listener. Without
// coalescing, a burst fans out N concurrent passes that all read the same stale
// roster, all run the authoritative `/members` reconcile, and all write + log the
// SAME change — a console + network + Dexie-write storm (the `rosterReconciled`
// leak). We collapse a burst to ONE running pass plus at most ONE trailing pass
// (so state that changed mid-flight is still picked up), which converges quietly.
const hydrateInFlight = new Map<string, Promise<boolean>>();
const hydratePending = new Set<string>();

/**
 * Resolve every member of a conversation by accountId and persist their
 * display name + avatar onto the Dexie row.
 *
 * - Group: merges into `memberDisplayNames` + `memberAvatarUris`.
 * - 1:1: updates the row `name` (only when it's still a placeholder) and
 *   `avatarUri` (the peer's picture).
 *
 * Coalesced per conversation (see `hydrateInFlight`): concurrent callers share the
 * in-flight pass; a call arriving mid-flight schedules exactly one trailing pass.
 *
 * Failures are swallowed — this is best-effort enrichment that must never
 * throw into a send/open path. Resolves to true if the row changed.
 */
export function hydrateMemberProfiles(conversationId: string): Promise<boolean> {
  const running = hydrateInFlight.get(conversationId);
  if (running !== undefined) {
    // A pass is already running for this conversation — don't start a second one.
    // Mark a single trailing pass so a mid-flight roster change is still caught,
    // and return the in-flight result to this caller.
    hydratePending.add(conversationId);
    return running;
  }
  const run = hydrateMemberProfilesImpl(conversationId).finally(() => {
    hydrateInFlight.delete(conversationId);
    if (hydratePending.delete(conversationId)) {
      // Exactly one trailing pass to reconcile anything that changed while the
      // first pass ran. Any further concurrent triggers coalesce onto THIS run.
      void hydrateMemberProfiles(conversationId);
    }
  });
  hydrateInFlight.set(conversationId, run);
  return run;
}

async function hydrateMemberProfilesImpl(conversationId: string): Promise<boolean> {
  try {
    // Gate on the authenticated session. Chat-list liveQuery effects mount and
    // fire this BEFORE bootstrap sets the access token, producing a burst of 401s
    // (profiles / members / devices) and wasted work. Once bootstrap authenticates
    // and loads conversations, the liveQuery re-emits and this re-runs with a
    // valid token. No token yet → skip; the re-emit retries.
    if (useSessionStore.getState().status.kind !== 'authenticated') return false;
    const row = await getConversationRow(conversationId);
    if (row === undefined) return false;

    // LEFT-GROUP GUARD (the #1 "group vanished after I left" cause). When the
    // local user has deliberately LEFT a group, `POST /leave` marks their
    // membership departed server-side, so `GET /v1/conversations/{id}/members`
    // authoritatively returns `not_member` for them. That verdict is IDENTICAL to
    // the ghost "New chat" a fresh device sees for a group the server never
    // admitted — and the orphan branches below would hide the row + drop the sync
    // cursor + (if empty) destroy the SDK group. A left group is NOT a ghost: the
    // non-destructive-leave design keeps it visible + read-only via `leftAt`
    // (conversationVisibility keeps `leftAt` rows, hides `orphaned` rows). So a
    // `leftAt` row must never be orphaned by hydration. It still reconciles its
    // roster/profiles for the read-only history view.
    const hasLeftGroup = ((row as { leftAt?: number }).leftAt ?? 0) > 0;

    if (row.kind === 'direct') {
      // AUTHORITATIVE group check — the ROOT fix for the "group avatar changes to
      // the peer's avatar" bleed. The MLS group meta (carried in the group's
      // GroupContext name) is the source of truth for group-vs-1:1, independent of
      // the local `kind`, which can lag (a single-peer group {me, B} joins as a
      // 1:1 with B before its meta is readable). A single-peer group is
      // roster-identical to a 1:1, so if we take the 1:1 path here we stamp B's
      // profile avatar as the "group avatar" on every hydration pass. Reading the
      // SDK meta NOW (it IS readable by hydration time) and healing the row makes
      // the stale local `kind` irrelevant.
      const sdkGroupMeta = readSdkGroupMeta(conversationId);
      if (sdkGroupMeta !== null) {
        await markConversationAsGroup(conversationId, sdkGroupMeta);
        return false;
      }
      let peer = row.peerAccountId;
      let r = isDirectoryAccountId(peer ?? '') ? await resolveOne(peer!) : null;
      // No resolvable 16-byte peer id (unset, or a 32-byte MLS UserId saved by an
      // older build, or the `ping.profile` broadcast never arrived) → ask the
      // SERVER for the conversation's account roster, the authoritative
      // broadcast-INDEPENDENT source. Pick the single non-self member, persist
      // it, and resolve its profile.
      if (r === null) {
        const { membership, peer: peerId } = await resolvePeerFromServerRoster(conversationId);
        // AUTHORITATIVE ORPHAN: the server says this device is NOT a member of
        // this conversation (403 `not_a_member` / 404). That's the ghost "New
        // chat" — a stale local MLS group the backend never admitted (an eager
        // `createConversation()` whose invite failed, rehydrated on reload). Hide
        // it via the shared server-orphan marker; it's recoverable if membership
        // ever lands (server relist / real message). Only on the DEFINITIVE
        // verdict — never on `unknown` (401 token-not-ready / network), which just
        // retries on the next pass — and only once past the settle window so an
        // in-flight create/join isn't orphaned before its membership is recorded.
        if (membership === 'not_member' && !hasLeftGroup && orphanSettled(row.createdAt)) {
          await markConversationServerOrphaned(conversationId, 'members_not_member');
          return false;
        }
        if (membership === 'not_member') return false;
        // GUARD: the row may have been reclassified as a GROUP while we were
        // resolving (the MLS group meta / Welcome upgrade lands asynchronously,
        // and `persistIfNew` upgrades a direct→group row). 1:1 writes
        // (peerAccountId, rename) applied to a group RENAME IT TO THE PEER — the
        // "single-peer group shows as Nile2" bug. Re-read and bail if it's no
        // longer a 1:1.
        if ((await getConversationRow(conversationId))?.kind !== 'direct') return false;
        if (peerId !== undefined && peerId !== peer) {
          await setConversationPeerAccountId(conversationId, peerId);
          peer = peerId;
          r = await resolveOne(peerId);
        }
      }
      if (peer === undefined || peer.length === 0 || r === null) return false;
      // Final guard before the rename/avatar/dedup writes: re-read the row. If a
      // concurrent group upgrade reclassified it, abandon ALL 1:1 writes (the
      // group branch hydrates it on the next pass). Root fix for a single-peer
      // group being renamed to its peer after it briefly shows the group title.
      const fresh = await getConversationRow(conversationId);
      if (fresh === undefined || fresh.kind !== 'direct') return false;
      // Defense-in-depth: a row durably marked a GROUP (`explicitGroup`) must
      // never take the 1:1 avatar/name path — that is exactly what stamped peer
      // B's avatar onto a single-peer group {me, B} and clobbered its group icon
      // on every pass. If the marker is set, leave all 1:1 writes to the group
      // branch even if `kind` momentarily still reads 'direct'.
      if ((fresh as { explicitGroup?: boolean }).explicitGroup === true) return false;
      // Now that we have a reliable peer account id, collapse any duplicate 1:1
      // rows for this peer (a re-invite mints a new MLS group → a second row).
      await dedupeDirectConversationsByPeer(peer);
      let changed = false;
      if (r.displayName !== undefined && isPlaceholderName(fresh.name)) {
        await setConversationName(conversationId, r.displayName);
        changed = true;
      }
      if (r.avatarUri !== undefined && r.avatarUri !== fresh.avatarUri) {
        await setConversationAvatar(conversationId, r.avatarUri);
        changed = true;
      }
      // Persist the STABLE media id behind the peer avatar (like a group icon) so
      // the picture can be re-resolved after the presigned `avatarUri` expires,
      // instead of decaying to initials. `setConversationAvatar` above already
      // wrote a fresh url this pass; this keeps the durable source in lockstep.
      if (r.avatarMediaId !== undefined && r.avatarMediaId !== fresh.avatarMediaId) {
        await setConversationAvatarMediaId(conversationId, r.avatarMediaId);
        changed = true;
      }
      // Persist the peer's KYC identity-verified status → drives the chat-list
      // verified check. Written even when false so a revoked verification clears.
      if ((r.identityVerified === true) !== (fresh.identityVerified === true)) {
        await setConversationIdentityVerified(conversationId, r.identityVerified === true);
        changed = true;
      }
      return changed;
    }

    // Refresh the GROUP's OWN avatar first. `avatarUri` is a short-lived
    // presigned S3 URL (expires ~5 min); persisting it once at join means the
    // group icon vanishes (falls back to initials) after it expires. So we
    // re-resolve a FRESH url from the stable `avatarMediaId` on every hydration
    // pass (boot / open / reconnect) — mirroring how 1:1 / member avatars stay
    // fresh via `resolveOne`. Best-effort; only writes when it actually changes.
    //
    // `avatarMediaId` is the SOURCE OF TRUTH for the group icon; `avatarUri` is
    // its resolved, cache-keyed form. Re-resolve it here (cheap, idempotent: a
    // given media id maps to a stable `cb=<id>` URL) so the icon survives a
    // presigned-URL expiry AND picks up an admin's icon CHANGE the moment the
    // new `avatarMediaId` lands — including the iOS→web path where the new id
    // arrives via `com.ping.group_name`. (We no longer special-case `data:`
    // URLs: the change handlers keep `avatarMediaId` in lockstep with the
    // avatar, so resolving the current id always yields the CURRENT image.)
    // AUTHORITATIVE GROUP ICON — heal any peer-avatar poison BEFORE resolving.
    // The group's icon media id lives in the MLS meta (`ping:meta:v1` →
    // avatarMediaId), stamped by the creator AND rewritten on every rename/avatar
    // change (`buildUpdatedGroupMetaName`), and readable by every joiner after the
    // Welcome. It is therefore the broadcast-independent source of truth for the
    // group icon. Trust it over the row's stored `avatarMediaId`, because a
    // single-peer group {me, B} is roster-identical to the 1:1 and is briefly
    // mis-seeded as a 1:1 before its meta is readable — during which the 1:1
    // hydration branch stamps B's PROFILE avatar media id onto the row. Resolving
    // that stored id here would render B's face as the group icon the moment the
    // chat is opened (the "group avatar becomes the other member's" bleed). So:
    //   * meta carries an avatarMediaId  -> heal the row to it (drops the poison),
    //   * meta says group but has NO icon -> a non-empty stored media id can only
    //     be peer poison; clear it (and its stale url) so the row falls back to
    //     initials, never a member's picture.
    let groupAvatarMediaId = row.avatarMediaId;
    const sdkMeta = readSdkGroupMeta(conversationId);
    if (sdkMeta !== null) {
      if (
        sdkMeta.avatarMediaId !== undefined &&
        sdkMeta.avatarMediaId.length > 0 &&
        sdkMeta.avatarMediaId !== row.avatarMediaId
      ) {
        await setConversationAvatarMediaId(conversationId, sdkMeta.avatarMediaId);
        groupAvatarMediaId = sdkMeta.avatarMediaId;
      } else if (
        sdkMeta.avatarMediaId === undefined &&
        row.avatarMediaId !== undefined &&
        row.avatarMediaId.length > 0
      ) {
        await clearConversationGroupAvatar(conversationId);
        groupAvatarMediaId = undefined;
      }
    }
    let groupAvatarChanged = false;
    if (groupAvatarMediaId !== undefined && groupAvatarMediaId.length > 0) {
      const fresh = withAvatarCacheKey(
        await fetchMediaDownloadUrl(groupAvatarMediaId).catch(() => undefined),
        groupAvatarMediaId,
      );
      if (fresh !== undefined && fresh.length > 0 && fresh !== row.avatarUri) {
        await setConversationAvatar(conversationId, fresh);
        groupAvatarChanged = true;
      }
    }

    // Group: resolve the full roster (minus self, which the host renders
    // separately) and merge the non-empty results.
    let ids = (row.memberAccountIds ?? []).filter((id) => isDirectoryAccountId(id));

    // AUTHORITATIVE ROSTER RECONCILE. The server (`GET /v1/conversations/{id}/
    // members`, `conversation_members`) is the source of truth for membership —
    // every member's device registers there. Reconcile the LOCAL roster against
    // it on every hydration pass (open / reconnect), not just when the local
    // roster is empty. This is what makes EVERY member see the SAME member list
    // regardless of which best-effort broadcasts (`com.ping.group_name` /
    // `com.ping.kick`) they happened to catch:
    //   * a member ADDED after our join (never in our MLS creation meta) appears,
    //   * a member who LEFT / was removed is pruned,
    //   * a member who missed the rename/add broadcast still converges.
    // Best-effort: on any failure we fall back to the local roster (still correct
    // for online members via the broadcasts). We only ACCEPT a non-empty server
    // response — an empty/failed read never prunes a good local roster.
    try {
      const self = getCurrentUserAccountId();
      const membership = await fetchConversationMembership(conversationId);
      // AUTHORITATIVE ORPHAN (same as the 1:1 path): a group the server says this
      // device is NOT a member of is a stale local MLS group the backend never
      // admitted — hide it via the shared server-orphan marker. Recoverable if
      // membership ever lands. Only on the definitive verdict, never `unknown`,
      // and only past the settle window so an in-flight create isn't orphaned.
      if (membership.kind === 'not_member') {
        // A deliberately-LEFT group legitimately reports not_member — keep it
        // visible + read-only (leftAt), never orphan/hide/destroy it.
        if (!hasLeftGroup && orphanSettled(row.createdAt)) {
          await markConversationServerOrphaned(conversationId, 'members_not_member_group');
        }
        return groupAvatarChanged;
      }
      // Exclude accounts we KNOW have left (kick/leave). The server may still
      // list a leaver until their MLS self-remove proposal is committed; without
      // this filter the authoritative reconcile below re-adds them and they
      // reappear in the member list right after "X left".
      const departed = new Set(row.departedAccountIds ?? []);
      const serverRoster = (membership.kind === 'member' ? membership.accountIds : []).filter(
        (id) => id !== self && isDirectoryAccountId(id) && !departed.has(id),
      );
      if (serverRoster.length > 0) {
        const serverSet = new Set(serverRoster);
        // Compare on the SAME basis as `serverRoster` — non-self, non-departed,
        // deduped. `ids` can transiently include self (the invite/group_name
        // union-merges carry the roster incl. self, and a null-identity moment
        // during hydration leaks it) or a just-departed account; comparing the raw
        // `ids` against the self/departed-excluded server set then reports a
        // phantom difference on EVERY pass and re-writes + re-logs forever (the
        // `rosterReconciled` loop). A leaked self is harmless to leave in the
        // stored array — the header count + Chat-Info list are derived from a
        // self-excluded set — so we only write when the REAL membership differs.
        const localSet = new Set(ids.filter((id) => id !== self && !departed.has(id)));
        const sameMembership =
          serverSet.size === localSet.size && [...serverSet].every((id) => localSet.has(id));
        if (!sameMembership) {
          // Replace with the server's authoritative set (adds joiners, drops
          // leavers). Surviving members keep their resolved names via the merge
          // below; pruned members simply fall out of the rendered roster.
          await setConversationMemberAccountIds(conversationId, serverRoster);
          log.info({
            event: 'memberProfiles.rosterReconciled',
            conversationIdPrefix: conversationId.slice(0, 8),
            before: localSet.size,
            after: serverRoster.length,
          });
        }
        ids = serverRoster;
      }
      // PRIMARY device-map source: the enriched `/members` response carries a
      // device→account map for every active member device (built by the SAME
      // query as the roster — no per-member fan-out). Merge it so message bubbles
      // + list previews resolve senders immediately. The `/v1/devices` backfill
      // below is then just the FALLBACK for an older backend that doesn't yet
      // return `member_devices` (map empty → backfill fills it per-member).
      if (membership.kind === 'member' && Object.keys(membership.deviceAccounts).length > 0) {
        await mergeMemberDeviceMaps(conversationId, {
          memberDeviceAccounts: { ...membership.deviceAccounts },
        });
      }
    } catch {
      // Server roster unavailable — keep the local roster. Non-fatal.
    }
    if (ids.length === 0) return groupAvatarChanged;
    const profiles = await resolveMany(ids);
    const merged =
      Object.keys(profiles).length > 0
        ? await mergeMemberProfiles(conversationId, profiles)
        : false;
    // Backfill the DEVICE-keyed maps authoritatively so group message bubbles and
    // the chat-list "Alice: hi" preview resolve a peer's name/avatar even when we
    // never received that device's `ping.profile`. Runs off the FRESHEST display
    // names (re-read post-merge) and the current device map, and is a network
    // no-op once every member's devices are already mapped. Best-effort — kept
    // independent of the directory-profile result above (device→account mapping
    // doesn't need a resolved display name).
    const post = await getConversationRow(conversationId);
    const deviceBackfilled =
      post !== undefined
        ? await backfillGroupDeviceMaps(
            conversationId,
            ids,
            post.memberDisplayNames ?? {},
            post.memberDeviceAccounts ?? {},
          )
        : false;
    return merged || groupAvatarChanged || deviceBackfilled;
  } catch (err) {
    log.warn({
      event: 'memberProfiles.hydrate.failed',
      conversationIdPrefix: conversationId.slice(0, 8),
      error: (err as Error).message,
    });
    return false;
  }
}

/**
 * Backstop sweep: hydrate every conversation whose row still looks unresolved —
 * a placeholder name ('New chat' / 'Group chat' / empty) or a missing avatar.
 * This heals rows that were synced in a PRIOR session (e.g. via device-linking)
 * and never opened, which is why a linked device shows "New chat" in the list.
 * Per-join hydration (`onConversationUpserted`) covers freshly-arriving rows;
 * this covers the ones already on disk. Bounded concurrency, fully best-effort.
 *
 * Call after auth bootstrap and after a linking sync completes. Idempotent.
 */
export async function hydrateAllConversations(): Promise<void> {
  let rows: Awaited<ReturnType<typeof listConversationRows>>;
  try {
    rows = await listConversationRows();
  } catch {
    return;
  }
  // Only touch rows that actually need it, so we don't fan out a directory
  // request for every conversation on every boot.
  const needsHydration = rows.filter((r) => {
    const nameMissing = isPlaceholderName(r.name);
    const avatarMissing = r.avatarUri === undefined || r.avatarUri.length === 0;
    // A group with a stable `avatarMediaId` ALWAYS needs a boot refresh even
    // when it already has an `avatarUri`: that URL is a short-lived presigned
    // link that expires between sessions, so we re-resolve a fresh one here.
    const groupAvatarStale =
      r.kind === 'group' && r.avatarMediaId !== undefined && r.avatarMediaId.length > 0;
    // A group whose DEVICE maps don't yet cover every member account needs a
    // boot pass too, so message-sender labels + chat-list previews resolve for
    // members we've never received a `ping.profile` from — even if the group's
    // own name/avatar are already resolved (which the checks above would miss).
    const groupDeviceMapsIncomplete =
      r.kind === 'group' &&
      (() => {
        const accountIds = r.memberAccountIds ?? [];
        if (accountIds.length === 0) return false;
        const mapped = new Set(Object.values(r.memberDeviceAccounts ?? {}));
        return accountIds.some((id) => id.length > 0 && !mapped.has(id));
      })();
    return nameMissing || avatarMissing || groupAvatarStale || groupDeviceMapsIncomplete;
  });
  if (needsHydration.length === 0) return;
  log.info({
    event: 'memberProfiles.hydrateAll.begin',
    total: rows.length,
    pending: needsHydration.length,
  });
  await mapLimited(needsHydration, 6, (r) => hydrateMemberProfiles(r.id).catch(() => false));
}
